/**
 * The small menu over whatever is selected. §6.3.
 *
 * Canva's: lock, duplicate, delete and "more", floating just above the object
 * rather than in a toolbar across the page, because these are the things done
 * to this object here. Below it instead when the object is too close to the
 * top for the menu to fit above. Out of the way while the object is being
 * dragged, and whenever something else owns the canvas - typing, cropping,
 * drawing, editing points.
 */

import { useEffect, useLayoutEffect, useState, type RefObject } from 'react';
import type Konva from 'konva';
import { useEditor } from '../state/store';
import { deleteSelected, duplicateSelected, toggleLock } from '../state/actions';
import { findLayer } from '../model/types';

/** Room the menu needs above an object, and the gap it keeps. */
const ABOVE = 52;

/** Anything this close to the top has the menu below it instead. */
const TOO_HIGH = 140;

export function SelectionMenu( {
  stage,
  onMenu,
  hidden,
}: {
  stage: RefObject<Konva.Stage | null>;
  onMenu?: ( at: { x: number; y: number } ) => void;
  hidden?: boolean;
} ) {
  const selection = useEditor( ( s ) => s.selection );
  const doc = useEditor( ( s ) => s.doc );
  const view = useEditor( ( s ) => s.view );
  const busy = useEditor( ( s ) => !! ( s.editing || s.cropping || s.shaping || s.sampling ) );
  const page = useEditor( ( s ) => s.doc?.pages[ s.pageIndex ] ?? null );

  const [ at, setAt ] = useState<{ x: number; y: number; below: boolean } | null>( null );
  const [ dragging, setDragging ] = useState( false );

  /*
   * Where the selection is on screen, asked of Konva: the union of each
   * selected object's box, turned objects included. Asked again whenever the
   * artwork redraws, not only when the document changes - a text measured
   * before its font has loaded has no size yet, and the menu sat over its
   * left edge until something else moved.
   */
  const [ drawn, setDrawn ] = useState( 0 );

  useEffect( () => {
    const artwork = stage.current?.getLayers()[ 0 ];

    if ( ! artwork ) return;

    let frame = 0;

    artwork.on( 'draw.tbmenu', () => {
      cancelAnimationFrame( frame );
      frame = requestAnimationFrame( () => setDrawn( ( n ) => n + 1 ) );
    } );

    return () => {
      cancelAnimationFrame( frame );
      artwork.off( 'draw.tbmenu' );
    };
  }, [ stage, doc === null ] );

  useLayoutEffect( () => {
    const node = stage.current;

    if ( ! node || selection.length === 0 ) {
      setAt( null );

      return;
    }

    let left = Infinity;
    let top = Infinity;
    let right = -Infinity;
    let bottom = -Infinity;

    for ( const id of selection ) {
      const shape = node.findOne( `.${ id }` );

      if ( ! shape ) continue;

      const box = shape.getClientRect();

      left = Math.min( left, box.x );
      top = Math.min( top, box.y );
      right = Math.max( right, box.x + box.width );
      bottom = Math.max( bottom, box.y + box.height );
    }

    if ( ! Number.isFinite( left ) ) {
      setAt( null );

      return;
    }

    const below = top < TOO_HIGH;

    // Below means under the rotate handle, which hangs beneath the object.
    const next = { x: Math.round( ( left + right ) / 2 ), y: Math.round( below ? bottom + 52 : top - ABOVE ), below };

    // Only when it moved: this runs on every redraw, and setting the same place
    // again would redraw for nothing.
    setAt( ( was ) => ( was && was.x === next.x && was.y === next.y && was.below === next.below ? was : next ) );
  }, [ selection, doc, view, stage, drawn ] );

  // Gone while dragging; back, re-measured, when the drag ends.
  useEffect( () => {
    const node = stage.current;

    if ( ! node ) return;

    const start = () => setDragging( true );
    const end = () => setDragging( false );

    node.on( 'dragstart.tbmenu', start );
    node.on( 'dragend.tbmenu', end );

    return () => {
      node.off( 'dragstart.tbmenu' );
      node.off( 'dragend.tbmenu' );
    };
  }, [ stage ] );

  if ( ! at || hidden || busy || dragging || view.preview || ! page ) return null;

  const layers = selection.map( ( id ) => findLayer( page.layers, id ) ).filter( Boolean );
  const locked = layers.length > 0 && layers.every( ( l ) => l?.locked );

  return (
    <div
      className={ `tb-objmenu${ at.below ? ' is-below' : '' }` }
      style={ { left: at.x, top: at.y } }
      role="toolbar"
      aria-label="Object actions"
    >
      <button
        type="button"
        className={ `tb-objmenu__btn${ locked ? ' is-on' : '' }` }
        aria-pressed={ locked }
        aria-label={ locked ? 'Unlock' : 'Lock' }
        title={ locked ? 'Unlock' : 'Lock' }
        onClick={ () => toggleLock() }
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
          <rect x="4.5" y="9" width="11" height="8" rx="1.5" />
          <path d={ locked ? 'M7 9V6.5a3 3 0 0 1 6 0V9' : 'M7 9V6.5a3 3 0 0 1 5.6-1.5' } />
        </svg>
      </button>
      <button type="button" className="tb-objmenu__btn" aria-label="Duplicate" title="Duplicate (Ctrl D)" onClick={ () => duplicateSelected() }>
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
          <rect x="6.5" y="6.5" width="10" height="10" rx="1.5" />
          <path d="M3.5 13V4.5a1 1 0 0 1 1-1H13" />
          <path d="M11.5 9.5v4M9.5 11.5h4" />
        </svg>
      </button>
      <button type="button" className="tb-objmenu__btn" aria-label="Delete" title="Delete" disabled={ locked } onClick={ () => deleteSelected() }>
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
          <path d="M3.5 5.5h13M8 5.5v-2h4v2M5.5 5.5l1 11h7l1-11" />
        </svg>
      </button>
      <button
        type="button"
        className="tb-objmenu__btn"
        aria-label="More"
        title="More"
        onClick={ ( event ) => {
          const box = event.currentTarget.getBoundingClientRect();

          onMenu?.( { x: box.left, y: box.bottom + 4 } );
        } }
      >
        <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <circle cx="4.5" cy="10" r="1.6" />
          <circle cx="10" cy="10" r="1.6" />
          <circle cx="15.5" cy="10" r="1.6" />
        </svg>
      </button>
    </div>
  );
}
